<template>
    <form v-if="!loading" @submit.prevent="checkoutRegister()">
        <SectionWrapper id="account" border-position="bottom">
            <template #title>
                {{ $t('plain_account') }}
            </template>

            <template #content>
                <div :class="[smallLayout ? '' : 'lg:grid-cols-2', 'grid grid-cols-1  gap-2 lg:-mt-2']">
                    <Input v-model:value="user.email" type="text" name="email" :form-component-id="$.uid" :label="$t('mail') + '*'" autocomplete="email" />
                    <Input
                        v-model:value="user.email_confirmation"
                        type="text"
                        name="email_confirmation"
                        :form-component-id="$.uid"
                        :label="$t('confirm_mail') + '*'"
                        :preventinsert="true"
                        autocomplete="new-password"
                    />
                    <Input
                        v-model:value="user.password"
                        type="password"
                        name="password"
                        :form-component-id="$.uid"
                        :label="$t('account_password') + '*'"
                        autocomplete="new-password"
                        :strength-meter="true"
                    />
                    <Input
                        v-model:value="user.password_confirmation"
                        type="password"
                        name="password_confirmation"
                        :form-component-id="$.uid"
                        :password-check="user.password"
                        :label="$t('confirm_password') + '*'"
                        autocomplete="new-password"
                        :strength-meter="true"
                        :preventinsert="true"
                    />
                </div>
            </template>
        </SectionWrapper>

        <SectionWrapper id="account" border-position="bottom">
            <template #title>
                {{ $t('contact_information') }}
            </template>

            <template #content>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:-mt-2">
                    <Toggle v-model:active="active_type" :choices="types" color="#031837" color-gradient="#031837" class="h-full col-span-2">
                        <template #choice="{ data }">
                            <div class="flex flex-col items-center justify-center h-full">
                                <span>
                                    {{ $translate(data.title) }}
                                </span>
                            </div>
                        </template>
                    </Toggle>

                    <Select
                        v-model:selected="user.salutation"
                        name="salutation"
                        :no-value-allowed="false"
                        :form-component-id="$.uid"
                        :label="$t('profile_salutation_') + '*'"
                        :options="salutations"
                        :multiple="false"
                        class="col-span-2"
                    >
                    </Select>

                    <Input
                        v-model:value="user.first_name"
                        type="text"
                        name="first_name"
                        :form-component-id="$.uid"
                        :label="$t('first_name') + '*'"
                        autocomplete="first-name"
                        class="col-span-2 lg:col-span-1"
                    >
                    </Input>

                    <Input
                        v-model:value="user.last_name"
                        type="text"
                        name="last_name"
                        :form-component-id="$.uid"
                        :label="$t('last_name') + '*'"
                        autocomplete="last-name"
                        class="col-span-2 lg:col-span-1"
                    >
                    </Input>
                    <Input
                        v-model:value="birthday_readable"
                        type="text"
                        name="birthday_at"
                        :form-component-id="$.uid"
                        :label="$t('acount_birthday')"
                        :mask="dateMask"
                        class="col-span-2 lg:col-span-1"
                    />
                    <Input
                        v-model:value="user.mobile"
                        type="text"
                        name="mobile"
                        :form-component-id="$.uid"
                        :label="$t('Mobile')"
                        autocomplete="mobile"
                        mask="+## (#) ### ### ## ###"
                        class="col-span-2 lg:col-span-1"
                    >
                    </Input>
                </div>
            </template>
        </SectionWrapper>

        <SectionWrapper id="address" border-position="bottom">
            <template #title>
                {{ $t('profile_adress') }}
            </template>

            <template #content>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:-mt-2">
                    <Input
                        v-model:value="user.street"
                        type="text"
                        name="street"
                        :form-component-id="$.uid"
                        :label="$t('profile_street and house number') + '*'"
                        autocomplete="street"
                        class="col-span-2"
                    >
                    </Input>
                    <Input
                        v-model:value="user.zipcode"
                        type="text"
                        name="zipcode"
                        :form-component-id="$.uid"
                        :label="$t('profile_zipcode') + '*'"
                        autocomplete="zipcode"
                        class="col-span-2 lg:col-span-1"
                    >
                    </Input>
                    <Input
                        v-model:value="user.location"
                        type="text"
                        name="location"
                        :form-component-id="$.uid"
                        :label="$t('profile_location') + '*'"
                        autocomplete="location"
                        class="col-span-2 lg:col-span-1"
                    >
                    </Input>
                    <Select
                        v-if="active_user_language"
                        name="country_id"
                        :no-value-allowed="false"
                        :form-component-id="$.uid"
                        :label="$t('profile_country')"
                        :selected="active_user_language"
                        :options="filteredCountries"
                        endpoint="/api/v1/countries/autocomplete"
                        :language="getActiveLanguage"
                        :multiple="false"
                        class="col-span-2"
                        @update:selected="user.country_id = $event"
                    >
                    </Select>
                </div>
            </template>
        </SectionWrapper>

        <TransitionExpand v-show="user.type === 'business'">
            <SectionWrapper v-if="user.type === 'business'" id="company" border-position="bottom">
                <template #title>
                    {{ $t('plain_company') }}
                </template>

                <template #content>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-2 -mt-2">
                        <Input
                            v-model:value="user.company"
                            type="text"
                            name="company"
                            :form-component-id="$.uid"
                            :label="$t('company_name') + '*'"
                            autocomplete="name"
                            class="col-span-2"
                        >
                        </Input>
                        <Input
                            v-model:value="user.tax_number"
                            type="text"
                            name="tax_number"
                            :form-component-id="$.uid"
                            :label="$t('tax_number') + '*'"
                            autocomplete="none"
                            class="col-span-2 lg:col-span-1"
                        >
                        </Input>
                        <Input
                            v-model:value="user.email_invoice"
                            type="text"
                            name="email_invoice"
                            :form-component-id="$.uid"
                            :label="$t('email_invoice')"
                            autocomplete="email"
                            class="col-span-2 lg:col-span-1"
                        >
                        </Input>
                        <Input
                            v-model:value="user.phone"
                            type="text"
                            name="phone"
                            :form-component-id="$.uid"
                            :label="$t('profile_phone')"
                            autocomplete="phone"
                            mask="+## (#) ### ### ## ###"
                            class="col-span-2 lg:col-span-1"
                        >
                        </Input>
                    </div>
                </template>
            </SectionWrapper>
        </TransitionExpand>

        <FormButton type="submit" class="my-6" :form-component-id="$.uid" styling="primary flex items-center gap-3" :value="$t('plain_register')" :disabled="loading">
            <FontAwesomeIcon
                v-show="loading"
                :icon="['fas', 'circle-notch']"
                class="animate-spin text-white transition-all ease-in-out duration-300 w-0"
                :class="loading ? 'w-10' : ''"
            >
            </FontAwesomeIcon>
        </FormButton>
    </form>
</template>

<script>
import { getActiveLanguage } from 'laravel-vue-i18n';
import { mapGetters } from 'vuex';
import i18next from 'i18next';
import { formatDateByRegion } from '../../helpers.js';

export default {
    props: {
        smallLayout: Boolean,
        redirectTo: {
            type: String
        }
    },
    data: () => ({
        user: {
            salutation: null
        },
        active_type: 0,
        loading: false,
        getActiveLanguage: getActiveLanguage(),
        filteredCountries: [],
        types: [],
        salutations: []
    }),

    computed: {
        dateFormat() {
            return window.meta?.region_configuration?.region === 'de' ? 'DD.MM.YYYY' : 'YYYY-MM-DD';
        },
        dateMask() {
            return window.meta?.region_configuration?.region === 'de' ? '##.##.####' : '####-##-##';
        },

        birthday_readable: {
            get() {
                if (!this.user?.birthday_at) return '';

                const parsed = this.$moment(this.user.birthday_at, 'YYYY-MM-DD');

                return parsed.isValid() ? parsed.format(this.dateFormat) : '';
            },
            set(value) {
                const inputFormat = this.dateFormat;
                const parsed = this.$moment(value, inputFormat, true);

                if (parsed.isValid()) {
                    this.user.birthday_at = parsed.format('YYYY-MM-DD');
                } else {
                    this.user.birthday_at = null;
                }
            }
        },

        ...mapGetters('countries', {
            countries: 'get'
        }),

        active_user_language() {
            if (this.countries.length) {
                if (this.user.country_id) {
                    return this.user.country_id;
                }

                const country = this.countries.find((country) => country.language.iso_3166_2 === this.getActiveLanguage)?.id;

                this.user.country_id = country;

                return this.user.country_id;
            }
        }
    },

    watch: {
        countries() {
            this.countries.forEach((country) => {
                const newCountry = {
                    label: country.name,
                    value: country.id
                };
                this.filteredCountries.push(newCountry);
            });
        },
        active_type: function () {
            this.user.type = this.types[this.active_type].value;
        }
    },

    mounted() {
        this.loading = true;

        this.$store.dispatch('countries/index').then(() => {
            this.loading = false;
        });

        this.salutations = [
            {
                value: 'male',
                label: i18next.t('profile_salutation_male')
            },
            {
                value: 'female',
                label: i18next.t('profile_salutation_female')
            }
        ];

        this.types = [
            {
                value: 'private',
                title: i18next.t('plain_private')
            },
            {
                value: 'business',
                title: i18next.t('plain_company')
            }
        ];

        this.user.language = getActiveLanguage();
        this.user.type = this.types[0]?.value;
        this.user.salutation = null;
    },

    methods: {
        checkoutRegister() {
            this.$store.commit('error/unsetByComponentId', this.$.uid);

            if (this.$moment(this.birthday_readable, this.dateFormat, true).isValid()) {
                this.user.birthday_at = formatDateByRegion(this.$moment(this.birthday_readable, this.dateFormat));
            } else {
                this.user.birthday_at = null;
            }

            // delete business fields if user type is private
            const data = this.user;

            if (this.user.type === 'private') {
                delete data.email_invoice;
                delete data.phone;
                delete data.tax_number;
                delete data.company;
            }

            this.loading = true;
            this.user.config = { componentId: this.$.uid };
            this.$store
                .dispatch('user/checkoutRegister', data)
                .then(() => {
                    window.scrollTo(0, 0);
                    this.loading = false;

                    if (this.redirectTo) {
                        this.$router.push(this.redirectTo);
                    }
                })
                .catch(() => {
                    this.loading = false;
                });
        }
    }
};
</script>
